/* ═════════════════════════ the calendar ═════════════════════════

   Three views of what is already fixed: the week, the month, and an agenda.
   Its content comes from S.events, from tasks that have been scheduled onto a
   clock, and from whatever the Google sync last brought back.

   It reads the calendar rather than deciding anything about it. What should
   happen when is the planner's business, two tabs along. */
import { standingMultiple } from '../state/evidence.js';
import { TZ } from '../core/time.js';
import { toast } from './dom.js';
import { subTotal } from './tasks.js';
import { COURSE_COLORS } from '../core/palette.js';
import { addDays, fmtDurMin, fmtWhen, isToday, stampLocal, stampUTC, startOfDay, startOfWeek, uid } from '../core/time.js';
import { DESK, SYNC, setSync } from '../platform/bridge.js';
import { S, courseColor, courseName, save } from '../state/store.js';
import { $, el } from './dom.js';
import { openFinish, showDeadlineList, subDone } from './tasks.js';
/** A task blocks exactly its estimate; an event blocks its real duration. */
export function itemWindow(it) {
  if (it.kind === 'event') return { start: it.start, end: it.end };
  return { start: it.start, end: it.start + it.estMin * 60000 };
}

/** The checklist as plain lines, so a calendar block carries the steps with it. */
export function subtaskLines(t) {
  if (!subTotal(t)) return [];
  return [`steps: ${subDone(t)}/${subTotal(t)} done`]
    .concat(t.subtasks.map((s) => `${s.done ? '☑' : '☐'} ${s.title}`));
}

export function gcalUrl(it) {
  const { start, end } = itemWindow(it);
  const isTask = it.kind !== 'event';
  const title = isTask ? `▢ ${it.title}` : it.title;
  const steps = isTask ? subtaskLines(it) : [];
  const details = isTask
    ? `studyHub TASK\ncourse: ${courseName(it.courseId)}\nestimated: ${fmtDurMin(it.estMin)}`
      + (standingMultiple() ? `\nprojected at ${standingMultiple().toFixed(2)}×: ${fmtDurMin(it.estMin * standingMultiple())}` : '')
      + (steps.length ? `\n\n${steps.join('\n')}` : '')
      + `\n\nThis block covers the ESTIMATE.`
    : `studyHub EVENT\ncourse: ${courseName(it.courseId)}${it.notes ? `\n${it.notes}` : ''}`;
  const p = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${stampLocal(start)}/${stampLocal(end)}`,
    details,
    ctz: TZ,
  });
  if (it.kind === 'event' && it.notes) p.set('location', it.notes);
  return 'https://calendar.google.com/calendar/render?' + p.toString();
}

export function openInGoogle(it) {
  window.open(gcalUrl(it), '_blank', 'noopener');
  toast('Google Calendar opened — press Save there to confirm');
}

/* Commas, semicolons and backslashes are field separators in an .ics — escape
   them per value, never over the joined string, or the \n line breaks we build
   would themselves get escaped into literal backslashes. */
export const icsText = (s) => String(s).replace(/([,;\\])/g, '\\$1');

export function icsFor(items) {
  const L = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//studyHub//EN', 'CALSCALE:GREGORIAN'];
  const now = stampUTC(Date.now());
  items.forEach((it) => {
    const { start, end } = itemWindow(it);
    const isTask = it.kind !== 'event';
    const steps = isTask ? subtaskLines(it).map(icsText) : [];
    const desc = isTask
      ? `studyHub TASK\\ncourse: ${icsText(courseName(it.courseId))}\\nestimated: ${fmtDurMin(it.estMin)}`
        + (steps.length ? `\\n\\n${steps.join('\\n')}` : '')
      : `studyHub EVENT\\ncourse: ${icsText(courseName(it.courseId))}${it.notes ? `\\n${icsText(it.notes)}` : ''}`;
    L.push('BEGIN:VEVENT',
      `UID:${it.id}@studyhub`,
      `DTSTAMP:${now}`,
      `DTSTART;TZID=${TZ}:${stampLocal(start)}`,
      `DTEND;TZID=${TZ}:${stampLocal(end)}`,
      `SUMMARY:${(isTask ? '▢ ' : '') + icsText(it.title)}`,
      `DESCRIPTION:${desc}`,
      `CATEGORIES:${isTask ? 'STUDYHUB-TASK' : 'STUDYHUB-EVENT'}`,
      'END:VEVENT');
  });
  L.push('END:VCALENDAR');
  return L.join('\r\n');
}

export function scheduledItems() {
  return [
    ...S.tasks.filter((t) => t.start && !t.done),
    ...S.events,
  ].sort((a, b) => a.start - b.start);
}

export function renderScheduled() {
  const wrap = $('scheduledList');
  wrap.textContent = '';
  const items = scheduledItems();
  if (!items.length) {
    wrap.appendChild(el('div', 'empty-list', 'Nothing scheduled. Tick “Block this time on Google Calendar” when adding a task, or add an event.'));
    return;
  }
  items.forEach((it) => {
    const isTask = it.kind !== 'event';
    const { start, end } = itemWindow(it);
    const n = el('div', 'sched-item');
    n.style.setProperty('--kind', isTask ? 'var(--accent)' : 'var(--accent-2)');
    n.appendChild(el('div', 'st-title', (isTask ? '▢ ' : '📅 ') + it.title));
    const dur = fmtDurMin((end - start) / 60000);
    const steps = isTask && subTotal(it) ? ` · ✓ ${subDone(it)}/${subTotal(it)} steps` : '';
    n.appendChild(el('div', 'st-meta',
      `${isTask ? 'TASK' : 'EVENT'} · ${courseName(it.courseId)}${steps}\n${fmtWhen(start)} · ${dur}${isTask ? ' (= estimate)' : ''}`));
    const a = el('div', 'st-actions');
    const g = el('button', 'btn', 'Add to Google ↗');
    g.addEventListener('click', () => openInGoogle(it));
    a.appendChild(g);
    if (isTask) {
      const f = el('button', 'btn btn-ghost', 'Finish');
      f.addEventListener('click', () => openFinish(it.id));
      a.appendChild(f);
    }
    n.appendChild(a);
    wrap.appendChild(n);
  });
}

/* ─────────────────── the Google bridge ───────────────────
   The old iframe embed only rendered for *public* calendars, which is why a
   Harvard Workspace account answered with “oops, we couldn't load your
   events”. Nothing in a page can fix that. So the real events arrive
   out-of-band: Calendar is read over MCP into ~/studyHub/data/google-sync.json,
   which is what we render below.

   Mail is not read, and no longer can be. Reading someone's inbox to guess at
   deadlines needs a restricted Gmail scope, which a shared web app cannot have
   without a verification review — and the feature was never worth that. A
   deadline you type in is a deadline you have read.                       */

export let syncBusy = false;

async function loadSync() {
  try {
    if (DESK) {
      setSync(await DESK.readSync());
    } else if (location.protocol === 'http:' || location.protocol === 'https:') {
      /* Served from somewhere, so there may be a sync file next to the page —
         the desktop shell writes one, and a local server will hand it over.
         A 404 just means nobody has synced, which `res.ok` turns into "never".

         Only over http(s), though. Opened from a file, or from the
         single-file build, there is no server to ask and the fetch throws
         outright: `URL scheme "file" is not supported`. It was caught, so the
         app was fine, but it logged an error on every load — which teaches
         whoever opens the console to ignore errors. */
      const res = await fetch('data/google-sync.json', { cache: 'no-store' });
      setSync(res.ok ? await res.json() : null);
    } else {
      setSync({ status: 'never' });
    }
  } catch {
    setSync({ status: 'never' });
  }
  renderSyncStatus();
  renderCalendar();
}

export function renderSyncStatus() {
  const dot = $('syncDot');
  const acct = $('syncAccount');
  const when = $('syncWhen');
  if (!dot) return;

  acct.textContent = SYNC.account || DESK?.account || 'no account linked';
  renderSyncButton();

  dot.className = 'sync-dot';
  if (syncBusy) {
    dot.classList.add('is-busy');
    when.textContent = 'syncing…';
    return;
  }
  if (SYNC.status === 'error') {
    dot.classList.add('is-bad');
    when.textContent = (SYNC.message || 'sync failed').slice(0, 70);
    return;
  }
  if (!SYNC.syncedAt) {
    when.textContent = 'never synced';
    return;
  }
  const ageMin = (Date.now() - SYNC.syncedAt) / 60000;
  dot.classList.add(ageMin > 180 ? 'is-stale' : 'is-ok');
  when.textContent = ageMin < 1 ? 'synced just now'
    : ageMin < 60 ? `synced ${Math.round(ageMin)}m ago`
    : ageMin < 1440 ? `synced ${Math.round(ageMin / 60)}h ago`
    : `synced ${Math.round(ageMin / 1440)}d ago`;
}

/* The button is the only thing that starts a sync, so it has to say what it is
   doing: disabled + spinning while the run is in flight, so a second click
   cannot queue a run main.js would only refuse. */
export function renderSyncButton() {
  const btn = $('calSync');
  if (!btn) return;
  btn.disabled = syncBusy;
  btn.classList.toggle('is-busy', syncBusy);
  $('calSyncLabel').textContent = syncBusy ? 'Syncing…' : 'Sync now';
  btn.title = syncBusy
    ? 'Claude is reading your calendar and inbox — this takes up to a minute.'
    : 'Pull Google Calendar and read new deadlines out of your inbox';
}

export function requestSync() {
  if (!DESK) {
    toast('Run “claude -p /studyhub-sync” in ~/studyHub, or use the desktop app.', true);
    return;
  }
  if (syncBusy) return;

  /* Paint the busy state immediately rather than waiting for main.js's
     sync:running event — the spawn is not instant and a dead button reads
     as a dropped click. */
  syncBusy = true;
  renderSyncStatus();

  DESK.requestSync().then((r) => {
    toast(r.message, !r.started);
    if (!r.started) { syncBusy = false; renderSyncStatus(); }
  }).catch((e) => {
    syncBusy = false;
    renderSyncStatus();
    toast(`Could not start sync: ${e.message}`, true);
  });
}

export let calAnchor = new Date();          // any day inside the visible range
export let calMode = 'WEEK';

/** Stable colour per source calendar, so the eye can group them. */
export function sourceColor(key) {
  if (!key) return COURSE_COLORS[5];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return COURSE_COLORS[h % COURSE_COLORS.length];
}

/** Google events + studyHub's own blocks, in one normalised shape. */
export function allCalendarItems() {
  /** @type {any[]} Google's rows and our own blocks: two shapes, one list. */
  const out = SYNC.events.map((e) => {
    const start = new Date(e.start).getTime();
    const end = e.end ? new Date(e.end).getTime() : start + 3600000;
    return {
      id: e.id || uid(),
      title: e.title || '(no title)',
      start, end,
      allDay: !!e.allDay,
      where: e.location || '',
      sub: e.calendar || 'Google Calendar',
      color: sourceColor(e.calendar || 'primary'),
      link: e.htmlLink || '',
      studyhub: false,
    };
  });

  scheduledItems().forEach((it) => {
    const { start, end } = itemWindow(it);
    const isTask = it.kind !== 'event';
    out.push({
      id: it.id,
      title: (isTask ? '▢ ' : '📅 ') + it.title,
      start, end,
      allDay: false,
      where: it.notes || '',
      sub: `studyHub · ${courseName(it.courseId)}`,
      color: courseColor(it.courseId),
      link: '',
      studyhub: true,
      ref: it,
    });
  });

  /* A deadline has no duration, so it gets a short marker window ending on the
     due moment — enough for the grid to place it at the right hour. */
  S.deadlines.forEach((d) => {
    out.push({
      id: d.id,
      title: '⚑ ' + d.title,
      start: d.due - 15 * 60000,
      end: d.due,
      allDay: false,
      where: d.notes || '',
      sub: `studyHub deadline · ${courseName(d.courseId)}`,
      color: courseColor(d.courseId),
      link: '',
      studyhub: true,
      deadline: true,
      ref: d,
    });
  });

  return out.sort((a, b) => a.start - b.start);
}

export const itemsBetween = (list, from, to) =>
  list.filter((e) => e.end > from.getTime() && e.start < to.getTime());

export function evNode(e, cls) {
  const n = el('div', cls || 'ev');
  n.style.setProperty('--ev', e.color);
  if (e.studyhub) n.classList.add('is-studyhub');
  if (e.deadline) n.classList.add('is-deadline');
  n.appendChild(el('div', 'ev-title', e.title));
  if (!e.allDay) {
    // a deadline's window is a marker, not a duration — show the moment it is due
    n.appendChild(el('div', 'ev-time',
      new Date(e.deadline ? e.end : e.start).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })));
  }
  n.title = `${e.title}\n${e.allDay ? 'all day' : fmtWhen(e.deadline ? e.end : e.start)}${e.where ? `\n${e.where}` : ''}\n${e.sub}`;
  n.addEventListener('click', () => {
    if (e.link) (DESK ? DESK.openExternal(e.link) : window.open(e.link, '_blank', 'noopener'));
    else if (e.deadline) showDeadlineList();
    else if (e.studyhub && e.ref && e.ref.kind !== 'event') openFinish(e.ref.id);
  });
  return n;
}

/** Side-by-side placement for events that overlap in time. */
export function layoutColumns(evs) {
  const sorted = [...evs].sort((a, b) => a.start - b.start || b.end - a.end);
  const placed = [];
  let cluster = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (!cluster.length) return;
    const cols = [];
    cluster.forEach((e) => {
      let ci = cols.findIndex((c) => c[c.length - 1].end <= e.start);
      if (ci === -1) { cols.push([e]); ci = cols.length - 1; } else cols[ci].push(e);
      e._col = ci;
    });
    cluster.forEach((e) => { e._cols = cols.length; placed.push(e); });
    cluster = [];
    clusterEnd = -Infinity;
  };

  sorted.forEach((e) => {
    if (e.start >= clusterEnd) flush();
    cluster.push(e);
    clusterEnd = Math.max(clusterEnd, e.end);
  });
  flush();
  return placed;
}

export const SLOT_H = 44;   // px per hour — must match .wk-slot in styles.css

export function renderWeek(host, items) {
  const from = startOfWeek(calAnchor);
  const to = addDays(from, 7);
  const days = Array.from({ length: 7 }, (_, i) => addDays(from, i));
  const visible = itemsBetween(items, from, to);

  const grid = el('div', 'wk');

  /* header */
  const head = el('div', 'wk-head');
  head.appendChild(el('div', 'wk-head-cell'));
  days.forEach((d) => {
    const c = el('div', 'wk-head-cell' + (isToday(d) ? ' is-today' : ''));
    c.appendChild(el('div', 'wk-dow', d.toLocaleDateString(undefined, { weekday: 'short' })));
    c.appendChild(el('div', 'wk-dom', String(d.getDate())));
    head.appendChild(c);
  });
  grid.appendChild(head);

  /* all-day strip — only when something needs it */
  const allDay = visible.filter((e) => e.allDay);
  let strip = null;
  if (allDay.length) {
    strip = el('div', 'wk-allday');
    strip.appendChild(el('div', 'wk-allday-label', 'all day'));
    days.forEach((d) => {
      const cell = el('div', 'wk-allday-cell');
      allDay.filter((e) => e.end > d.getTime() && e.start < addDays(d, 1).getTime())
        .forEach((e) => cell.appendChild(evNode(e, 'ev is-chip')));
      strip.appendChild(cell);
    });
    grid.appendChild(strip);
  }

  /* hour gutter */
  const gutter = el('div', 'wk-gutter');
  for (let h = 0; h < 24; h++) {
    gutter.appendChild(el('div', 'wk-hour-label', h === 0 ? '' :
      new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: 'numeric' })));
  }
  grid.appendChild(gutter);

  /* day columns */
  days.forEach((d) => {
    const col = el('div', 'wk-col' + (isToday(d) ? ' is-today' : ''));
    for (let h = 0; h < 24; h++) col.appendChild(el('div', 'wk-slot'));

    const dayStart = d.getTime();
    const dayEnd = addDays(d, 1).getTime();
    const timed = visible.filter((e) => !e.allDay && e.end > dayStart && e.start < dayEnd);

    layoutColumns(timed).forEach((e) => {
      const s = Math.max(e.start, dayStart);
      const en = Math.min(e.end, dayEnd);
      const top = ((s - dayStart) / 3600000) * SLOT_H;
      const height = Math.max(18, ((en - s) / 3600000) * SLOT_H - 2);
      const n = evNode(e);
      n.style.top = `${top}px`;
      n.style.height = `${height}px`;
      const w = 100 / e._cols;
      n.style.left = `calc(${e._col * w}% + 2px)`;
      n.style.right = 'auto';
      n.style.width = `calc(${w}% - 4px)`;
      col.appendChild(n);
    });

    if (isToday(d)) {
      const now = new Date();
      const line = el('div', 'wk-now');
      line.style.top = `${((now.getTime() - startOfDay(now).getTime()) / 3600000) * SLOT_H}px`;
      col.appendChild(line);
    }
    grid.appendChild(col);
  });

  host.appendChild(grid);

  requestAnimationFrame(() => {
    // pin the all-day row just under the sticky header
    if (strip) strip.style.top = `${head.offsetHeight}px`;
    // open on the working day, not on midnight
    host.scrollTop = Math.max(0, 8 * SLOT_H - 40);
  });

  return `${from.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – `
       + `${addDays(from, 6).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

export function renderMonth(host, items) {
  const first = new Date(calAnchor.getFullYear(), calAnchor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const weeks = Math.ceil((((new Date(calAnchor.getFullYear(), calAnchor.getMonth() + 1, 0)).getDate())
    + ((first.getDay() + 6) % 7)) / 7);
  const grid = el('div', 'mo');

  ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    .forEach((d) => grid.appendChild(el('div', 'mo-dow', d)));

  for (let i = 0; i < weeks * 7; i++) {
    const d = addDays(gridStart, i);
    const cell = el('div', 'mo-cell'
      + (d.getMonth() !== calAnchor.getMonth() ? ' is-out' : '')
      + (isToday(d) ? ' is-today' : ''));
    cell.appendChild(el('div', 'mo-num', String(d.getDate())));

    const dayItems = itemsBetween(items, d, addDays(d, 1));
    dayItems.slice(0, 3).forEach((e) => cell.appendChild(evNode(e, 'ev is-chip')));
    if (dayItems.length > 3) cell.appendChild(el('div', 'mo-more', `+${dayItems.length - 3} more`));
    grid.appendChild(cell);
  }

  host.appendChild(grid);
  return calAnchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export function renderAgenda(host, items) {
  const from = startOfDay(calAnchor);
  const to = addDays(from, 28);
  const visible = itemsBetween(items, from, to);

  const wrap = el('div', 'ag');
  if (!visible.length) {
    wrap.appendChild(el('div', 'empty-list', 'Nothing on the calendar for the next four weeks.'));
    host.appendChild(wrap);
    return `${from.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${addDays(from, 27).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }

  for (let i = 0; i < 28; i++) {
    const d = addDays(from, i);
    const dayItems = itemsBetween(visible, d, addDays(d, 1));
    if (!dayItems.length) continue;

    const block = el('div', 'ag-day');
    block.appendChild(el('div', 'ag-date' + (isToday(d) ? ' is-today' : ''),
      d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
      + (isToday(d) ? ' · today' : '')));

    dayItems.forEach((e) => {
      const row = el('div', 'ag-row');
      row.style.setProperty('--ev', e.color);
      const t = e.allDay ? 'all day'
        : `${new Date(e.start).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} – `
          + `${new Date(e.end).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
      row.appendChild(el('div', 'ag-when', t));
      const what = el('div', 'ag-what');
      what.appendChild(el('div', 'ag-title', e.title));
      what.appendChild(el('div', 'ag-sub', [e.sub, e.where].filter(Boolean).join(' · ')));
      row.appendChild(what);
      row.addEventListener('click', () => {
        if (e.link) (DESK ? DESK.openExternal(e.link) : window.open(e.link, '_blank', 'noopener'));
        else if (e.studyhub && e.ref && e.ref.kind !== 'event') openFinish(e.ref.id);
      });
      block.appendChild(row);
    });
    wrap.appendChild(block);
  }

  host.appendChild(wrap);
  return `${from.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${addDays(from, 27).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

export function renderCalendar() {
  const host = $('calView');
  if (!host) return;
  host.textContent = '';

  const items = allCalendarItems();
  const linked = SYNC.status === 'ok' || SYNC.events.length > 0;

  // The "not linked yet" card only takes over when there is genuinely nothing
  // to show — studyHub's own blocks still render before the first Google sync.
  $('calEmpty').hidden = linked || items.length > 0;

  let range = '';
  if (calMode === 'MONTH') range = renderMonth(host, items);
  else if (calMode === 'AGENDA') range = renderAgenda(host, items);
  else range = renderWeek(host, items);

  $('calRange').textContent = range;
  [...$('calModeSeg').children].forEach((b) => b.classList.toggle('is-active', b.dataset.mode === calMode));
}

export function stepCalendar(dir) {
  if (calMode === 'MONTH') calAnchor = new Date(calAnchor.getFullYear(), calAnchor.getMonth() + dir, 1);
  else if (calMode === 'AGENDA') calAnchor = addDays(calAnchor, dir * 28);
  else calAnchor = addDays(calAnchor, dir * 7);
  renderCalendar();
}

export function initCalendarTab() {
  calMode = S.cal.mode || 'WEEK';

  [...$('calModeSeg').children].forEach((b) => b.addEventListener('click', () => {
    calMode = b.dataset.mode;
    S.cal.mode = calMode; save();
    renderCalendar();
  }));

  $('calPrev').addEventListener('click', () => stepCalendar(-1));
  $('calNext').addEventListener('click', () => stepCalendar(1));
  $('calToday').addEventListener('click', () => { calAnchor = new Date(); renderCalendar(); });
  $('calSync').addEventListener('click', requestSync);

  $('calOpen').addEventListener('click', () => {
    const url = 'https://calendar.google.com/calendar/r';
    DESK ? DESK.openExternal(url) : window.open(url, '_blank', 'noopener');
  });

  $('calExport').addEventListener('click', () => {
    const items = scheduledItems();
    if (!items.length) return toast('Nothing scheduled to export.', true);
    const blob = new Blob([icsFor(items)], { type: 'text/calendar;charset=utf-8' });
    const a = el('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'studyhub.ics';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    toast(`Exported ${items.length} item(s) — import into Google Calendar`);
  });

  /* live updates from the sync file */
  if (DESK) {
    DESK.onSyncChanged((data) => {
      setSync(data);
      renderSyncStatus(); renderCalendar();
    });
    DESK.onSyncRunning((r) => { syncBusy = r; renderSyncStatus(); });
    DESK.onSyncDone((r) => toast(r.message, !r.ok));
  } else {
    setInterval(loadSync, 60000);   // browser fallback: poll the served file
  }

  loadSync();
  setInterval(() => { if (S.ui.tab === 'calendar' && calMode === 'WEEK') renderCalendar(); }, 60000);
}

/* ═════════════════════ 5. LONGTERM PLANNING ═════════════════════
   Every other tab is about today. This one is about the term.

   Two layers, deliberately separate:

     · The horizon is computed here, on the spot, from what you have typed in.
       It answers "when does everything land", and it is right the moment you
       add something. It cannot tell you when to *start*, because effort is
       booked against the week a thing is due.

     · The plan answers exactly that. It takes what the app already knows —
       what this kind of work has really cost you, and where the hours in a day
       actually are once the lectures are out of them — and works backwards
       from each due date to the last day the thing could begin. It runs here,
       in milliseconds, off the same state every other tab writes to.        */
